import type { QueryClient } from "@tanstack/react-query"
import { isAbortError } from "@/shared/api/errors"
import type { AccessState } from "../model/access-state"
import { computeAccessState } from "../model/access-state"
import type { User } from "../model/types"
import { enforceAccessPolicy } from "./access-policy"
import { currentIdentity, type Resolution } from "./identity"
import { resolveUser } from "./resolve-user"

export { getRedirectTargetForAccessState } from "./access-policy"
export { cacheAuthResult } from "./cache-auth-result"
export { persistAuthResult } from "./persist-auth-result"
export { revalidateUser } from "./revalidate-user"
export { userQueryOptions } from "./user-query"
export { currentIdentity }

export interface AuthResult {
	user: User | null
	userId: number | null
	isAuthenticated: boolean
	accessState: AccessState
}

interface AuthenticateParams {
	queryClient: QueryClient
	locationSearch: Record<string, unknown>
	pathname: string
}

function userOf(resolution: Resolution): User | null {
	if (resolution.status !== "resolved") return null
	return resolution.user
}

function toAccessState(resolution: Resolution): AccessState {
	if (resolution.status === "rejected") return { kind: "rejected" }
	return computeAccessState(userOf(resolution))
}

function userIdOf(user: User | null): number | null {
	if (user === null) return null
	return user.id
}

export async function authenticateUser({
	queryClient,
	locationSearch,
	pathname,
}: AuthenticateParams): Promise<AuthResult> {
	let resolution: Resolution = { status: "anonymous" }
	try {
		resolution = await resolveUser({ queryClient, locationSearch })
	} catch (error) {
		if (!isAbortError(error)) throw error
	}

	const accessState = toAccessState(resolution)
	enforceAccessPolicy(accessState, pathname)

	const user = userOf(resolution)
	return {
		user,
		userId: userIdOf(user),
		isAuthenticated: accessState.kind === "authenticated",
		accessState,
	}
}
